import {
  Action,
  combineReducers,
  configureStore,
  Reducer,
} from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import { setupListeners } from "@reduxjs/toolkit/query";
import {
  PAUSE,
  PERSIST,
  FLUSH,
  persistReducer,
  REHYDRATE,
  PURGE,
  REGISTER,
  persistStore,
} from "redux-persist";
import autoMergeLevel2 from "redux-persist/es/stateReconciler/autoMergeLevel2";
import counterReducer from "./slices/counterSlice";
import { statesApi } from "./api/states";
import { usersApi } from "./api/users";
import { documentsApi } from "./api/documents";
import { videosApi } from "./api/videos";
import { answersApi } from "./api/answers";
import { questionnaireApi } from "./api/questionnaire";
import { paymentsApi } from "./api/payments";
import { attorneysApi } from "./api/attorneys";
import { lawFirmsApi } from "./api/law-firms";
import { clauseDraftsApi } from "./api/clauseDrafts";
import { fdAnswersApi } from "./api/fdAnswers";
import { financialDisclosuresApi } from "./api/financials/financialDisclosures";
import { attorneyPayoutsApi } from "./api/admin/attorney-payouts";
import { sectionDraftsApi } from "./api/sectionDrafts";
import { documentLocksApi } from "./api/documentLocks";
import { documentAnalysisApi } from "./api/documentAnalysis";
import { libraryGroupsApi } from "./api/libraryGroups";
import { draftsSyncApi } from "./api/draftSync";
import { attorneyReviewsApi } from "./api/attorneyReviews";
import { stripeAccountApi } from "./api/stripe-account";
import { templatesApi } from "./api/templates";
import { discountsApi } from "./api/discounts";

const appReducer = combineReducers({
  counter: counterReducer,
  [statesApi.reducerPath]: statesApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [documentsApi.reducerPath]: documentsApi.reducer,
  [videosApi.reducerPath]: videosApi.reducer,
  [answersApi.reducerPath]: answersApi.reducer,
  [questionnaireApi.reducerPath]: questionnaireApi.reducer,
  [paymentsApi.reducerPath]: paymentsApi.reducer,
  [attorneysApi.reducerPath]: attorneysApi.reducer,
  [lawFirmsApi.reducerPath]: lawFirmsApi.reducer,
  [clauseDraftsApi.reducerPath]: clauseDraftsApi.reducer,
  [sectionDraftsApi.reducerPath]: sectionDraftsApi.reducer,
  [financialDisclosuresApi.reducerPath]: financialDisclosuresApi.reducer,
  [fdAnswersApi.reducerPath]: fdAnswersApi.reducer,
  [attorneyPayoutsApi.reducerPath]: attorneyPayoutsApi.reducer,
  [documentLocksApi.reducerPath]: documentLocksApi.reducer,
  [documentAnalysisApi.reducerPath]: documentAnalysisApi.reducer,
  [libraryGroupsApi.reducerPath]: libraryGroupsApi.reducer,
  [attorneyReviewsApi.reducerPath]: attorneyReviewsApi.reducer,
  [stripeAccountApi.reducerPath]: stripeAccountApi.reducer,
  [templatesApi.reducerPath]: templatesApi.reducer,
  [discountsApi.reducerPath]: discountsApi.reducer,
  [draftsSyncApi.reducerPath]: draftsSyncApi.reducer,
});

export type RootState = ReturnType<typeof appReducer>;

const rootReducer: Reducer<RootState> = (state, action: Action) => {
  if (action.type === "USER_LOGOUT") {
    // Don't use await here, just trigger the removal
    void storage.removeItem("persist:root");
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const persistConfig = {
  key: "root",
  storage,
  stateReconciler: autoMergeLevel2,
  whitelist: [""],
};

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

export function setupStore(preloadedState?: Partial<RootState>) {
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(
        statesApi.middleware,
        usersApi.middleware,
        documentsApi.middleware,
        videosApi.middleware,
        answersApi.middleware,
        questionnaireApi.middleware,
        paymentsApi.middleware,
        attorneysApi.middleware,
        lawFirmsApi.middleware,
        clauseDraftsApi.middleware,
        sectionDraftsApi.middleware,
        financialDisclosuresApi.middleware,
        fdAnswersApi.middleware,
        attorneyPayoutsApi.middleware,
        documentLocksApi.middleware,
        documentAnalysisApi.middleware,
        draftsSyncApi.middleware,
        libraryGroupsApi.middleware,
        attorneyReviewsApi.middleware,
        stripeAccountApi.middleware,
        templatesApi.middleware,
        discountsApi.middleware,
      ),
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    preloadedState: preloadedState as any,
  });
}

export const store = setupStore();
setupListeners(store.dispatch);
export const persistor = persistStore(store);
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
